import { useEffect, FC } from 'react';
import { useNavigate } from 'react-router-dom';

interface TimedRedirectProps {
  to?: string;
  delayInSeconds?: number;
  onLogout?: () => void;
}

const AUTO_LOGOUT_KEY = 'userLastActive';
const ACTIVITY_SYNC_KEY = 'userActivity';

const AutoLogout: FC<TimedRedirectProps> = ({
  to = '/login',
  delayInSeconds = 30 * 60, // 30 minutes in seconds
  onLogout,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    let timer: NodeJS.Timeout;

    const handleLogout = () => {
      // Cleanup ApexCharts instances
      if (window.ApexCharts) {
        const charts = document.querySelectorAll('.apexcharts-canvas');
        charts.forEach(chart => {
          const chartId = chart.id;
          if (chartId) {
            const chartInstance = window.ApexCharts.getChartByID(
              chartId.split('-')[0],
            );
            if (chartInstance) {
              chartInstance.destroy();
            }
          }
        });
      }

      // Clear last activity timestamp
      localStorage.removeItem(AUTO_LOGOUT_KEY);

      // Execute custom logout callback if provided
      if (onLogout) {
        onLogout();
      }

      // Navigate to logout page
      navigate(to);
    };

    const checkForInactivity = () => {
      const lastActive = localStorage.getItem(AUTO_LOGOUT_KEY);
      if (!lastActive) {
        handleLogout();
        return;
      }

      const timeElapsed = Date.now() - parseInt(lastActive, 10);
      if (timeElapsed > delayInSeconds * 1000) {
        handleLogout();
      } else {
        // Set next check
        timer = setTimeout(checkForInactivity, 1000);
      }
    };

    const updateUserActivity = () => {
      localStorage.setItem(AUTO_LOGOUT_KEY, Date.now().toString());
      // Broadcast activity to other tabs
      localStorage.setItem(ACTIVITY_SYNC_KEY, Date.now().toString());
    };

    // Handle storage events from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACTIVITY_SYNC_KEY) {
        // Another tab has activity, update last active time
        localStorage.setItem(AUTO_LOGOUT_KEY, Date.now().toString());
      }
    };

    // Add event listeners for user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
    ];

    // Initialize last active time
    updateUserActivity();

    // Start checking for inactivity
    timer = setTimeout(checkForInactivity, 1000);

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, updateUserActivity);
    });

    // Add storage event listener for cross-tab communication
    window.addEventListener('storage', handleStorageChange);

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateUserActivity);
      });
      window.removeEventListener('storage', handleStorageChange);
      clearTimeout(timer);
    };
  }, [navigate, to, delayInSeconds, onLogout]);

  return null;
};

// Add type definition for ApexCharts global object
declare global {
  interface Window {
    ApexCharts: {
      getChartByID: (id: string) => {
        destroy: () => void;
      } | null;
    };
  }
}

export default AutoLogout;
